import {
  countPagesRead,
  isAnswerUnderway,
  isClarificationAnsweredPart,
  isNarrationPart,
  isStreamedPart,
  isToolPartRunning,
  isTraceToolType,
  lastItemBesideCompanyList,
  TRACE_STEP_KIND_BY_TOOL_TYPE,
  type AssistantPart,
  type AssistantRenderItem,
  type ResearchPart,
  type ThinkingStepKind,
  type TraceToolType,
} from "@/lib/chat-render-items";
import { sourceLink } from "@/lib/source-link";
import {
  CHAT_TRACE_EVENT_PART_TYPE,
  CHAT_TRACE_TIMELINE_PART_TYPE,
  type ChatTraceTimelineData,
  type ChatTraceTimelineEvent,
  type CompanyRegistryToolOutput,
  type ReadPagesToolInput,
  type ReadPagesToolOutput,
  type WebSearchBatchToolInput,
  type WebSearchBatchToolOutput,
  type WebSearchToolInput,
  type WebSearchToolOutput,
  type WebSearchToolProgress,
  type WebSearchToolSource,
} from "@shaarp/common";
import type { ToolUIPart } from "ai";

export type { ThinkingStepKind };

export type ThinkingStepStatus = "running" | "done" | "error" | "interrupted";

export type ThinkingStep = {
  /** Stable while the turn streams: rows only ever add up at the end. */
  readonly id: string;
  readonly kind: ThinkingStepKind;
  readonly status: ThinkingStepStatus;
  readonly counts: StepCounts;
  /** Sites the step's pages come from, in the order they were first seen. */
  readonly domains: readonly string[];
  /** What the step's detail shows. Never on screen until the reader asks. */
  readonly parts: readonly ResearchPart[];
  /**
   * The line the model wrote to announce what it does next, on a reflection:
   * answer text in the reader's language, shown under the row's name.
   */
  readonly narration?: string;
  /** The team tactics a tactics step applied, by title, each named once. */
  readonly tacticTitles?: readonly string[];
};

/** What a step counted, the numbers its row and the card's header show. */
export type StepCounts = {
  /** Queries run, across every call of a web-search step. */
  readonly queryCount: number;
  /** Pages a reading step reads, or has read. */
  readonly pageCount: number;
  /**
   * Every page a search step turned up, the number that climbs while the
   * reader waits. It does not drop when a call settles: a settled call counts
   * every page it found, not only the ones it kept.
   */
  readonly foundCount: number;
  /** Pages or registry rows the step handed over: what the reader can open. */
  readonly sourceCount: number;
  /**
   * Companies read off the pages, as the multi-round search of older
   * conversations reported them. The current tool reports none.
   */
  readonly companyCount: number;
};

const NO_COUNTS: StepCounts = {
  queryCount: 0,
  pageCount: 0,
  foundCount: 0,
  sourceCount: 0,
  companyCount: 0,
};

/** The counts of several steps added up: a step that came back counts once. */
export function sumCounts(steps: readonly ThinkingStep[]): StepCounts {
  return steps.reduce<StepCounts>(
    (total, { counts }) => ({
      queryCount: total.queryCount + counts.queryCount,
      pageCount: total.pageCount + counts.pageCount,
      foundCount: total.foundCount + counts.foundCount,
      sourceCount: total.sourceCount + counts.sourceCount,
      companyCount: total.companyCount + counts.companyCount,
    }),
    NO_COUNTS
  );
}

/** The kinds of the search rows, whose counters the card's header adds up. */
export const SEARCH_STEP_KINDS: ReadonlySet<ThinkingStepKind> = new Set([
  "webSearch",
  "readPages",
  "registry",
]);

/** A call that ended without an answer: failed, or refused. */
export function isFailedToolPart(part: { readonly state?: string }): boolean {
  return part.state === "output-error" || part.state === "output-denied";
}

/** The page a site visit read, as the model asked for it. */
export function getScrapedUrl(part: AssistantPart): string | undefined {
  return ((part as ToolUIPart).input as { url?: string } | undefined)?.url;
}

/** The tools whose row's detail is drawn by a view of its own. */
const OWN_DETAIL_TOOL_TYPES: ReadonlySet<string> = new Set([
  "tool-webSearch",
  "tool-searchCompanyRegistry",
  "tool-readPages",
  "tool-scrapeWebsite",
]);

/**
 * The input fields a tool's row may show in its detail: search criteria
 * only. A field not listed never reaches the screen — a contact's email, name
 * or LinkedIn, an internal id — and a tool added to the card shows nothing
 * until it lists its own. The tools with a view of their own list none.
 */
const CRITERIA_FIELDS_BY_TOOL_TYPE: Readonly<Record<TraceToolType, readonly string[]>> = {
  "tool-webSearch": [],
  "tool-readPages": [],
  "tool-scrapeWebsite": [],
  "tool-searchCompanyRegistry": [],
  "tool-getCompanyData": ["name", "domain", "siret"],
  "tool-searchHubspotCompanies": ["query"],
  "tool-searchSeliarisCompanies": ["query"],
  "tool-listUserTactics": [],
  "tool-searchUserTactics": ["toSearch"],
  "tool-activateUserTactic": [],
  "tool-searchPeople": ["query", "targetCount"],
  "tool-previewContactReveal": [],
};

/** A field value as one line: a list joined, an object or an empty value left out. */
function readableValue(value: unknown): string | null {
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    const items = value
      .map(readableValue)
      .filter((item): item is string => Boolean(item));
    return items.length > 0 ? items.join(", ") : null;
  }
  return null;
}

/**
 * What a tool was asked for, as its row's detail shows it: the fields it
 * lists as criteria, by their input name, each with a value to show.
 */
export function getToolCriteria(
  part: AssistantPart
): (readonly [field: string, value: string])[] {
  if (!isTraceToolType(part.type)) return [];
  const input = (part as ToolUIPart).input;
  if (!input || typeof input !== "object") return [];
  return CRITERIA_FIELDS_BY_TOOL_TYPE[part.type].flatMap((field) => {
    const value = readableValue((input as Record<string, unknown>)[field]);
    return value ? [[field, value] as const] : [];
  });
}

/**
 * Whether a row has a detail to unfold. A reasoning part can come with no
 * text — some providers send the step without its summary — and a tool with
 * no criteria to show has none either: a detail with nothing in it is no
 * detail. A narration shows under the row, not in it.
 */
export function hasStepDetail(step: ThinkingStep): boolean {
  if (step.kind === "clarification") return false;
  return step.parts.some(({ part }) => {
    if (!isStreamedPart(part)) return false;
    if (part.type === "reasoning") return part.text.trim().length > 0;
    return OWN_DETAIL_TOOL_TYPES.has(part.type) || getToolCriteria(part).length > 0;
  });
}

export type ThinkingTrace = {
  /** The steps in the order they happened; a step that comes back is a new row. */
  readonly steps: readonly ThinkingStep[];
  /**
   * The step the header names: a live answer always has one at work until
   * its text starts — the last one started, when calls run side by side.
   * Null once the answer is being written, or over.
   */
  readonly activeStep: ThinkingStep | null;
  readonly isRunning: boolean;
};

export type ThinkingTraceOptions = {
  /** The message is the one being streamed. */
  readonly isLive: boolean;
  /** Nothing was written after the trace: it is the wait the reader watches. */
  readonly isTrailing: boolean;
  /** The message holds answer text, wherever it sits. */
  readonly hasAnswerText: boolean;
  /** The turn answers the questions the previous one asked. */
  readonly answersClarification: boolean;
};

/**
 * What a part actually carries, against the contract the API produces: a call
 * still streaming holds only part of its input, the batch tool sends
 * `queries`, and messages persisted before it carry `seedQueries` or a single
 * `query`.
 */
type WebSearchPartInput = Partial<WebSearchBatchToolInput> &
  Partial<WebSearchToolInput> & {
    query?: string;
  };

/**
 * A running search's snapshot carries the progress fields, the settled output
 * the rest.
 */
type WebSearchPartOutput = Partial<WebSearchBatchToolOutput> &
  Partial<WebSearchToolOutput> &
  Partial<WebSearchToolProgress>;

/**
 * The queries a web search ran: its output once settled, else its input.
 */
export function getWebSearchQueries(part: AssistantPart): string[] {
  const toolPart = part as ToolUIPart;
  const input = toolPart.input as WebSearchPartInput | undefined;
  const output = toolPart.output as WebSearchPartOutput | undefined;
  if (output?.queries?.length) return output.queries;
  if (input?.queries?.length) return input.queries;
  if (input?.seedQueries?.length) return input.seedQueries;
  return input?.query ? [input.query] : [];
}

/** The ranked sources a web search kept. Empty until the tool settles. */
export function getWebSearchSources(part: AssistantPart): WebSearchToolSource[] {
  const output = (part as ToolUIPart).output as WebSearchPartOutput | undefined;
  return output?.sources ?? [];
}

/**
 * The registry rows a call brought back. The output is a plain message when the
 * criteria could not be searched (no place, no matching activity code), so the
 * rows are only read off an object.
 */
export function getRegistryOutput(
  part: AssistantPart
): CompanyRegistryToolOutput | undefined {
  const output = (part as ToolUIPart).output;
  return output && typeof output === "object"
    ? (output as CompanyRegistryToolOutput)
    : undefined;
}

/** Every page or company a settled part lists, whichever tool found it. */
export function getPartUrls(part: AssistantPart): string[] {
  if (part.type === "tool-readPages") {
    const output = (part as ToolUIPart).output as
      | Partial<ReadPagesToolOutput>
      | undefined;
    return (output?.pages ?? []).map((page) => page.url);
  }
  if (part.type === "tool-scrapeWebsite") {
    const url = getScrapedUrl(part);
    return url ? [url] : [];
  }
  if (part.type === "tool-searchCompanyRegistry") {
    return (getRegistryOutput(part)?.companies ?? []).map(
      (company) => company.profileUrl
    );
  }
  return getWebSearchSources(part).map((source) => source.url);
}

/** The pages a reading call was asked for: known before it settles. */
function getRequestedUrls(part: ToolUIPart): string[] {
  if (part.type === "tool-readPages") {
    return (part.input as Partial<ReadPagesToolInput> | undefined)?.urls ?? [];
  }
  return getPartUrls(part);
}

/** Pages a search found, the same count while it runs and once it settled. */
function foundSourceCount(part: ToolUIPart): number {
  const output = part.output as WebSearchPartOutput | undefined;
  return (
    output?.sourcesSoFar ??
    output?.totalSourcesFound ??
    output?.sources?.length ??
    0
  );
}

function unique<T>(values: readonly T[]): T[] {
  return [...new Set(values)];
}

function stepStatus(
  toolParts: readonly ToolUIPart[],
  isLive: boolean
): ThinkingStepStatus {
  if (toolParts.some(isToolPartRunning)) {
    // A part left pending on a stream that no longer runs (stopped by the
    // user, provider error, reload mid-run) never settles on its own: only the
    // live stream may spin, and the green check would claim a success.
    return isLive ? "running" : "interrupted";
  }
  const failed = toolParts.filter(isFailedToolPart);
  return failed.length > 0 && failed.length === toolParts.length
    ? "error"
    : "done";
}

type Row = {
  kind: ThinkingStepKind;
  parts: ResearchPart[];
  narration?: string;
};

function buildToolStep(
  id: string,
  kind: ThinkingStepKind,
  indexed: readonly ResearchPart[],
  isLive: boolean
): ThinkingStep {
  const toolParts = indexed.map(({ part }) => part as ToolUIPart);
  const running = toolParts.filter(isToolPartRunning);
  const settled = toolParts.filter((part) => !isToolPartRunning(part));
  const urls = [
    ...running.flatMap(getRequestedUrls),
    ...settled.flatMap(getPartUrls),
  ];
  // A running search names its sites in its snapshots: its pages come later.
  const reportedDomains = toolParts.flatMap(
    (part) => (part.output as WebSearchPartOutput | undefined)?.domainsSoFar ?? []
  );
  return {
    id,
    kind,
    status: stepStatus(toolParts, isLive),
    counts: {
    // One call fans out many queries, so the count follows queries, not calls.
    queryCount:
      kind === "webSearch"
        ? toolParts.reduce(
            (total, part) =>
              total + Math.max(getWebSearchQueries(part).length, 1),
            0
          )
        : 0,
    pageCount:
      kind === "readPages"
        ? countPagesRead(settled) +
          running.reduce(
            (total, part) => total + getRequestedUrls(part).length,
            0
          )
        : 0,
    foundCount:
      kind === "webSearch"
        ? toolParts.reduce((total, part) => total + foundSourceCount(part), 0)
        : 0,
    sourceCount:
      kind === "webSearch" || kind === "registry" ? unique(urls).length : 0,
    companyCount:
      kind === "webSearch"
        ? toolParts.reduce((total, part) => {
            const output = part.output as WebSearchPartOutput | undefined;
            return total + (output?.companyCount ?? output?.companiesSoFar ?? 0);
          }, 0)
        : 0,
    },
    domains:
      kind === "webSearch" || kind === "readPages"
        ? unique([
            ...reportedDomains,
            ...urls
              .map((url) => sourceLink(url)?.host)
              .filter((host): host is string => Boolean(host)),
          ])
        : [],
    parts: indexed,
    ...(kind === "tactics" && { tacticTitles: appliedTacticTitles(toolParts) }),
  };
}

/** The titles of the tactics a step applied, each once, in order. */
function appliedTacticTitles(toolParts: readonly ToolUIPart[]): string[] {
  return unique(
    toolParts
      .filter((part) => part.type === "tool-activateUserTactic")
      .map((part) => (part.output as { title?: unknown } | undefined)?.title)
      .filter((title): title is string => typeof title === "string" && title.length > 0)
  );
}

function plainStep(
  id: string,
  kind: ThinkingStepKind,
  status: ThinkingStepStatus,
  parts: readonly ResearchPart[]
): ThinkingStep {
  return {
    id,
    kind,
    status,
    counts: NO_COUNTS,
    domains: [],
    parts,
  };
}

/**
 * The rows of a turn, in the order they happened.
 *
 * Every model step opens with a reflection — the stream's `step-start` marks
 * it, its reasoning summary fills it — and the calls it makes follow it, one
 * row per tool. A step that comes back later is written again where it came
 * back. Persisted messages lose the step markers: their reasoning alone marks
 * where the model thought.
 */
function layOutRows(parts: readonly ResearchPart[]): Row[] {
  const rows: Row[] = [];
  const last = (): Row | undefined => rows[rows.length - 1];
  const reflection = (): Row => {
    const row = last();
    if (row?.kind === "reflection") return row;
    const opened: Row = { kind: "reflection", parts: [] };
    rows.push(opened);
    return opened;
  };

  // The model thinks before its first call even when no marker says so.
  if (!parts.some(({ part }) => part.type === "step-start")) reflection();
  for (const indexed of parts) {
    const { type } = indexed.part;
    if (type === "step-start") {
      reflection();
      continue;
    }
    if (type === "reasoning") {
      reflection().parts.push(indexed);
      continue;
    }
    if (isClarificationAnsweredPart(indexed.part)) {
      rows.push({ kind: "clarification", parts: [] });
      continue;
    }
    if (isNarrationPart(indexed.part)) {
      const row = reflection();
      const { text } = indexed.part;
      row.narration = row.narration ? `${row.narration} ${text}` : text;
      continue;
    }
    if (!isTraceToolType(type)) continue;
    const kind = TRACE_STEP_KIND_BY_TOOL_TYPE[type];
    // Calls one model step makes side by side share their row.
    const row = last();
    if (row?.kind === kind) {
      row.parts.push(indexed);
    } else {
      rows.push({ kind, parts: [indexed] });
    }
  }
  return rows;
}

/**
 * The steps a turn went through, in the order they happened, and the one at
 * work. The card ends where the answer starts: writing it is not a step.
 */
export function buildThinkingTrace(
  parts: readonly ResearchPart[],
  {
    isLive,
    isTrailing,
    hasAnswerText,
    answersClarification,
  }: ThinkingTraceOptions
): ThinkingTrace {
  const rows = layOutRows(parts);
  const isWritingAnswer = hasAnswerText && !isTrailing;
  const isCallRunning = (row: Row) =>
    row.kind !== "reflection" &&
    row.parts.some(({ part }) => isToolPartRunning(part as ToolUIPart));
  const hasCallRunning = isLive && rows.some(isCallRunning);
  // Between the last call coming back and the next step showing up, nothing
  // streams: the model is reading what it found, which is a reflection. The
  // row it waits in becomes the one the next step opens, at the same place.
  // A line being written at the end of the card is text, maybe the answer's
  // first words: no row counts the time it takes, as for the answer itself.
  const isWritingLine = Boolean(rows[rows.length - 1]?.narration);
  const isReflecting =
    isLive && !hasCallRunning && !isWritingAnswer && !isWritingLine;
  if (isReflecting && rows[rows.length - 1]?.kind !== "reflection") {
    rows.push({ kind: "reflection", parts: [] });
  }

  const offset = answersClarification ? 1 : 0;
  const steps: ThinkingStep[] = rows.map((row, index) => {
    const id = `row-${index + offset}`;
    if (row.kind === "clarification") {
      return plainStep(id, "clarification", "done", []);
    }
    if (row.kind !== "reflection") {
      return buildToolStep(id, row.kind, row.parts, isLive);
    }
    const isActive = isReflecting && index === rows.length - 1;
    return {
      ...plainStep(id, "reflection", isActive ? "running" : "done", row.parts),
      ...(row.narration && { narration: row.narration }),
    };
  });
  if (answersClarification) {
    steps.unshift(plainStep("row-0", "clarification", "done", []));
  }

  // Two calls of different tools can run side by side, each row timed on its
  // own: the header names the last one started.
  const activeStep =
    steps.filter((step) => step.status === "running").pop() ?? null;
  return { steps, activeStep, isRunning: activeStep !== null };
}

type ResearchGroupItem = Extract<AssistantRenderItem, { kind: "researchGroup" }>;

/** What an answer did before writing, as its first card of work shows it. */
export type TurnWork = {
  /** The answer's first card, if it did any work. */
  readonly firstGroup: ResearchGroupItem | undefined;
  /** Nothing follows that card yet — or, with no card, nothing at all. */
  readonly isTrailing: boolean;
  readonly hasAnswerText: boolean;
  /**
   * Live, a short line may only announce the next call: the answer is under
   * way once what it wrote is plainly the answer.
   */
  readonly isAnswerUnderway: boolean;
  readonly timeline: ChatTraceTimelineData | null;
};

/**
 * Reads an answer's work off its render items: the card it starts with, and
 * how far the writing after it has gone. The card of a turn picked up after a
 * clarification reads the next answer's work the same way.
 */
export function describeTurnWork(
  parts: readonly AssistantPart[],
  renderItems: readonly AssistantRenderItem[],
  isLive: boolean
): TurnWork {
  const trailingItem = lastItemBesideCompanyList(renderItems);
  const firstGroup = renderItems.find(
    (item): item is ResearchGroupItem => item.kind === "researchGroup"
  );
  const texts = renderItems.flatMap((item) =>
    item.kind === "part" && item.part.type === "text" ? [item.part.text] : []
  );
  return {
    firstGroup,
    isTrailing: firstGroup
      ? firstGroup === trailingItem
      : trailingItem === undefined,
    hasAnswerText: texts.length > 0,
    isAnswerUnderway: texts.some((text) => !isLive || isAnswerUnderway(text)),
    timeline: readTraceTimeline(parts),
  };
}

/**
 * The timeline a message carries, if it does: whole once stored, or — live —
 * sent once, empty, with each event in a part of its own after it.
 */
export function readTraceTimeline(
  parts: readonly AssistantPart[]
): ChatTraceTimelineData | null {
  const part = parts.find(
    (candidate) => candidate.type === CHAT_TRACE_TIMELINE_PART_TYPE
  ) as { data?: Partial<ChatTraceTimelineData> } | undefined;
  const data = part?.data;
  if (!data || typeof data.startedAt !== "number" || !Array.isArray(data.events)) {
    return null;
  }
  const sentEvents = parts.flatMap((candidate) =>
    candidate.type === CHAT_TRACE_EVENT_PART_TYPE
      ? [(candidate as { data: ChatTraceTimelineEvent }).data]
      : []
  );
  return sentEvents.length > 0
    ? { startedAt: data.startedAt, events: [...data.events, ...sentEvents] }
    : (data as ChatTraceTimelineData);
}

const STEP_MARKER = { type: "step-start" } as AssistantPart;

/**
 * The card's parts with the step markers a reload lost put back from the
 * timeline, so a stored answer shows the reflections it showed live. A live
 * message keeps its own markers.
 *
 * A marker goes right after the call it follows: the reasoning written in the
 * new step comes after it, as on the wire.
 */
export function withTimelineSteps(
  parts: readonly ResearchPart[],
  timeline: ChatTraceTimelineData | null
): readonly ResearchPart[] {
  if (!timeline || parts.some(({ part }) => part.type === "step-start")) {
    return parts;
  }
  // The calls a step opened before, and whether one opened after the last.
  const steppedBefore = new Set<string>();
  let isStepPending = false;
  for (const event of timeline.events) {
    if (event.type === "step") isStepPending = true;
    if (event.type === "toolStart") {
      if (isStepPending) steppedBefore.add(event.toolCallId);
      isStepPending = false;
    }
  }

  const marker = (): ResearchPart => ({ part: STEP_MARKER, index: -1 });
  const result: ResearchPart[] = [];
  let lastCallPosition = -1;
  for (const indexed of parts) {
    const toolCallId = (indexed.part as Partial<ToolUIPart>).toolCallId;
    if (toolCallId && steppedBefore.has(toolCallId)) {
      result.splice(lastCallPosition + 1, 0, marker());
    }
    result.push(indexed);
    if (toolCallId) lastCallPosition = result.length - 1;
  }
  if (isStepPending) result.splice(lastCallPosition + 1, 0, marker());
  return result;
}

export type TraceDurations = {
  /** Time each row ran, by row id, over the passes that ended. */
  readonly finishedMs: Readonly<Record<string, number>>;
  /**
   * The rows at work, in row order, and since when on the server's clock:
   * the calls one step makes run side by side.
   */
  readonly running: readonly { readonly id: string; readonly since: number }[];
  /**
   * The wait itself: the time any row was at work, the time calls shared
   * counted once. Since when it runs, if it still does.
   */
  readonly total: { readonly finishedMs: number; readonly runningSince: number | null };
};

/**
 * The time each row ran, replayed from the timeline. A call row runs while
 * one of its calls does, side by side with any other; a reflection runs while
 * no call of the card does, from the moment the last one settles, below every
 * row that already ran. Text pauses it — a line announcing a call included —
 * and the answer itself stops it: only a call of the card puts it back to
 * work then, what the answer calls after that is not the card's. A call the
 * card has no row for times nothing.
 */
export function computeTraceDurations(
  steps: readonly ThinkingStep[],
  timeline: ChatTraceTimelineData,
  {
    fromIndex = 0,
    isOver = false,
  }: {
    /** The first row of the turn the timeline is from, when a card shows two. */
    readonly fromIndex?: number;
    /** No event will follow: what still runs stopped at the last one. */
    readonly isOver?: boolean;
  } = {}
): TraceDurations {
  const rowOfCall = new Map<string, number>();
  steps.forEach((step, index) => {
    for (const { part } of step.parts) {
      const toolCallId = (part as Partial<ToolUIPart>).toolCallId;
      if (toolCallId) rowOfCall.set(toolCallId, index);
    }
  });
  const reflectionFrom = (from: number): number | null => {
    const found = steps.findIndex(
      (step, index) => index >= from && step.kind === "reflection"
    );
    return found === -1 ? null : found;
  };

  const finishedMs: Record<string, number> = {};
  /** Since when each row at work runs, by row index. */
  const sinceByRow = new Map<number, number>();
  const runningCallsByRow = new Map<number, Set<string>>();
  // Held in an object: the closures below move it, and a plain `let` would
  // read as never reassigned to the type checker.
  const clock: {
    reflection: number | null;
    /** The lowest row the next reflection may open: never above one that ran. */
    floor: number;
    hasAnswerStarted: boolean;
    busySince: number | null;
    busyMs: number;
  } = { reflection: null, floor: fromIndex, hasAnswerStarted: false, busySince: null, busyMs: 0 };

  const start = (index: number, at: number): void => {
    if (!sinceByRow.has(index)) sinceByRow.set(index, at);
    clock.floor = Math.max(clock.floor, index);
  };
  const stop = (index: number, at: number): void => {
    const since = sinceByRow.get(index);
    if (since === undefined) return;
    const { id } = steps[index];
    finishedMs[id] = (finishedMs[id] ?? 0) + Math.max(0, at - since);
    sinceByRow.delete(index);
  };
  const openReflection = (from: number, at: number): void => {
    const index = reflectionFrom(from);
    if (index === null) return;
    clock.reflection = index;
    start(index, at);
  };
  const closeReflection = (at: number): void => {
    if (clock.reflection === null) return;
    stop(clock.reflection, at);
    clock.reflection = null;
  };
  const isCallRunning = (): boolean => runningCallsByRow.size > 0;
  const tallyBusy = (at: number): void => {
    if (sinceByRow.size > 0 && clock.busySince === null) {
      clock.busySince = at;
    } else if (sinceByRow.size === 0 && clock.busySince !== null) {
      clock.busyMs += Math.max(0, at - clock.busySince);
      clock.busySince = null;
    }
  };

  openReflection(fromIndex, timeline.startedAt);
  tallyBusy(timeline.startedAt);
  for (const event of timeline.events) {
    if (event.type === "toolStart") {
      const index = rowOfCall.get(event.toolCallId);
      if (index !== undefined) {
        closeReflection(event.at);
        clock.hasAnswerStarted = false;
        const calls = runningCallsByRow.get(index) ?? new Set<string>();
        calls.add(event.toolCallId);
        runningCallsByRow.set(index, calls);
        start(index, event.at);
      }
    } else if (event.type === "toolEnd") {
      const index = rowOfCall.get(event.toolCallId);
      const calls = index === undefined ? undefined : runningCallsByRow.get(index);
      if (index !== undefined && calls?.delete(event.toolCallId)) {
        if (calls.size === 0) {
          runningCallsByRow.delete(index);
          stop(index, event.at);
        }
        if (!isCallRunning() && !clock.hasAnswerStarted) {
          openReflection(clock.floor + 1, event.at);
        }
      }
    } else if (event.type === "step") {
      if (!isCallRunning() && clock.reflection === null && !clock.hasAnswerStarted) {
        openReflection(clock.floor, event.at);
      }
    } else {
      // Writing pauses the reflection; only the answer itself ends it.
      closeReflection(event.at);
      if (event.type === "answer") clock.hasAnswerStarted = true;
    }
    tallyBusy(event.at);
  }

  if (isOver) {
    const lastAt = timeline.events[timeline.events.length - 1]?.at ?? timeline.startedAt;
    for (const index of [...sinceByRow.keys()]) stop(index, lastAt);
    tallyBusy(lastAt);
  }
  return {
    finishedMs,
    running: [...sinceByRow]
      .sort(([a], [b]) => a - b)
      .map(([index, since]) => ({ id: steps[index].id, since })),
    total: { finishedMs: clock.busyMs, runningSince: clock.busySince },
  };
}
